import { useMutation, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'

import { removeProductFromFavoriteList } from '@/gen/bff'
import { getAllFavoriteListIdsByProductKeys } from '@/hooks/bff/favorite/getAllFavoriteListIdsByProducts/queryKeys'
import { getDefaultFavoriteListKeys } from '@/hooks/bff/favorite/getDefaultFavoriteList/queryKeys'
import { getFavoriteListFullKeys } from '@/hooks/bff/favorite/getFavoriteListFull/queryKeys'
import { useOrgScopeSnapshot } from '@/hooks/useOrgScopeSnapshot'
import { useTranslations } from '@/hooks/useTranslations'

interface RemoveProductFromFavoriteListParams {
  favoriteListId: string
  productId: string
}

export const useRemoveProductFromFavoriteList = () => {
  const { translate } = useTranslations()
  const queryClient = useQueryClient()
  const snapshotOrgScope = useOrgScopeSnapshot()

  return useMutation({
    mutationFn: ({ productId, favoriteListId }: RemoveProductFromFavoriteListParams) =>
      removeProductFromFavoriteList({ path: { productId: productId, favoriteListId: favoriteListId } }),
    onMutate: snapshotOrgScope,
    onSuccess: (_data, { productId }, context) => {
      queryClient.invalidateQueries({ queryKey: getDefaultFavoriteListKeys.all })
      queryClient.invalidateQueries({ queryKey: getFavoriteListFullKeys.all })
      // Per-product invalidation avoids refetching every cached product across every organisation visited this session.
      queryClient.invalidateQueries({
        queryKey: getAllFavoriteListIdsByProductKeys.product(context.customerId, context.orgUnitId, productId),
      })

      enqueueSnackbar(translate('favoriteList.removeProduct'), {
        variant: 'success',
      })
    },
    meta: {
      errorMessage: translate('common.api_error'),
    },
  })
}
